/**
 * ImmersiveDiary.jsx — World-class mobile diary writing experience
 *
 * FIX: Spaces were being stripped when reading contentEditable text.
 *   - getPlainText now walks the DOM and converts <br>/block elements
 *     to newlines, preserving every space character exactly.
 *   - Editor div gets white-space:pre-wrap so spaces never collapse.
 *   - Debug block removed from EntryDetail.
 *   - EntryDetail body uses white-space:pre-wrap so saved text renders correctly.
 */

import { useState, useRef, useCallback, useEffect } from "react";
import { auth } from "../../firebase";
import { authFetch } from "../../utils/authFetch";
import { useNavigate } from "react-router-dom";

// ═══ PAPER VIBES ═══
const VIBES = [
  {
    id: "classic",
    name: "Classic",
    swatch: "linear-gradient(135deg, #faf8f3, #f3efe6)",
    bg: "#faf8f3",
    paper: "linear-gradient(#faf8f3 0px, #faf8f3 31px, #d4c9b8 31px, #d4c9b8 32px)",
    ink: "#2a2420",
    inkSoft: "#6b6055",
    placeholder: "#c4b8a8",
    accent: "#5A6993",
    titleColor: "#1a1612",
    margin: "#e8b4b4",
    cardBg: "#ffffff",
    border: "#e8e2d6",
  },
  {
    id: "midnight",
    name: "Midnight",
    swatch: "linear-gradient(135deg, #1a1d2e, #252940)",
    bg: "#1a1d2e",
    paper: "linear-gradient(#1a1d2e 0px, #1a1d2e 31px, #2e3248 31px, #2e3248 32px)",
    ink: "#d4d0c8",
    inkSoft: "#8a8690",
    placeholder: "#4a4860",
    accent: "#8ea4c8",
    titleColor: "#e8e4dc",
    margin: "#3d2840",
    cardBg: "#252940",
    border: "#353858",
  },
  {
    id: "sunset",
    name: "Sunset",
    swatch: "linear-gradient(135deg, #fef5ee, #fdebd8)",
    bg: "#fef5ee",
    paper: "linear-gradient(#fef5ee 0px, #fef5ee 31px, #f0d4b8 31px, #f0d4b8 32px)",
    ink: "#3a2820",
    inkSoft: "#8a6e58",
    placeholder: "#d4b8a0",
    accent: "#c97b4b",
    titleColor: "#2a1810",
    margin: "#e8a070",
    cardBg: "#ffffff",
    border: "#f0dcc8",
  },
  {
    id: "forest",
    name: "Forest",
    swatch: "linear-gradient(135deg, #f2f7f2, #e4efe4)",
    bg: "#f2f7f2",
    paper: "linear-gradient(#f2f7f2 0px, #f2f7f2 31px, #c4d8c4 31px, #c4d8c4 32px)",
    ink: "#1a2a1a",
    inkSoft: "#5a705a",
    placeholder: "#a0b8a0",
    accent: "#4a7c5c",
    titleColor: "#0a1a0a",
    margin: "#7ab87a",
    cardBg: "#ffffff",
    border: "#d4e4d4",
  },
  {
    id: "lavender",
    name: "Lavender",
    swatch: "linear-gradient(135deg, #f5f0fa, #ebe0f5)",
    bg: "#f5f0fa",
    paper: "linear-gradient(#f5f0fa 0px, #f5f0fa 31px, #d4c4e4 31px, #d4c4e4 32px)",
    ink: "#2a2035",
    inkSoft: "#6a5a78",
    placeholder: "#b0a0c0",
    accent: "#7c5a9c",
    titleColor: "#1a1025",
    margin: "#c4a0d4",
    cardBg: "#ffffff",
    border: "#e0d4ec",
  },
];

const MOOD_COLORS = {
  happy: "#4caf50", grateful: "#66bb6a", hopeful: "#26a69a", calm: "#42a5f5",
  neutral: "#78909c", tired: "#8d6e63", sad: "#5c6bc0", anxious: "#ef5350",
  overwhelmed: "#e53935", angry: "#d32f2f", crisis: "#1976d2",
};
const MOOD_EMOJI = {
  happy: "😊", grateful: "🙏", hopeful: "🌱", calm: "😌", neutral: "😐",
  tired: "😴", sad: "😢", anxious: "😰", overwhelmed: "😩", angry: "😤", crisis: "💙",
};

export default function ImmersiveDiary({ onEntrySaved }) {
  const navigate = useNavigate();
  const [vibe, setVibe] = useState(VIBES[0]);
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [limitReached, setLimitReached] = useState(false);
  const [insight, setInsight] = useState(null);
  const [saved, setSaved] = useState(false);
  //const [wordCount, setWordCount] = useState(0);
  const wordCountRef = useRef(null);
  const [hasText, setHasText] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const editorRef = useRef(null);

  // ── Date ──
  const today = new Date();
  const dateStr = today.toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric", year: "numeric"
  });

  /**
   * getPlainText — walks the contentEditable DOM tree and reconstructs
   * the text with spaces fully intact.  Using .innerText alone can
   * collapse sequences of spaces in some browsers/environments, and
   * it does NOT preserve <br> as newlines in all cases.
   */
  const getPlainText = useCallback(() => {
    if (!editorRef.current) return "";

    const walk = (node) => {
      // 1. If it's text, grab it and convert ALL weird spaces to normal spaces
      if (node.nodeType === Node.TEXT_NODE) {
        // \u00A0 is the Non-Breaking Space that contentEditable loves to use.
        // \u200B is a zero-width space sometimes left behind by keyboards.
        return node.data
          .replace(/[\u00A0\u202F]/g, " ") 
          .replace(/[\u200B-\u200D\uFEFF]/g, ""); 
      }

      const tag = node.nodeName?.toLowerCase();

      // <br> → newline
      if (tag === "br") return "\n";

      // Block-level elements get a newline
      const BLOCKS = new Set([
        "p", "div", "li", "ul", "ol", "blockquote",
        "h1", "h2", "h3", "h4", "h5", "h6",
      ]);

      let text = "";
      for (const child of node.childNodes) {
        text += walk(child);
      }

      if (BLOCKS.has(tag)) {
        if (text && !text.endsWith("\n")) text += "\n";
      }

      return text;
    };

    return walk(editorRef.current).replace(/\n$/, "");
  }, []);

  // ── Word count ──
const updateWordCount = useCallback(() => {
  const t = getPlainText().trim();
  const count = t ? t.split(/\s+/).filter(Boolean).length : 0;
  
  // Update the text directly in the DOM
  if (wordCountRef.current) {
    wordCountRef.current.innerText = `${count} ${count === 1 ? "word" : "words"}`;
  }

  // Only trigger a React re-render when going from empty to having text (or vice versa)
  // This ensures the Save button enables/disables without breaking your spaces!
  const currentlyHasText = count > 0;
  if (hasText !== currentlyHasText) {
    setHasText(currentlyHasText);
  }
}, [getPlainText, hasText]);
  // ── Format commands ──
  const toggleBold = useCallback(() => {
    document.execCommand("bold", false, null);
    setIsBold(!isBold);
    editorRef.current?.focus();
  }, [isBold]);

  const toggleItalic = useCallback(() => {
    document.execCommand("italic", false, null);
    setIsItalic(!isItalic);
    editorRef.current?.focus();
  }, [isItalic]);

  // ── Track selection state ──
  useEffect(() => {
    const handler = () => {
      setIsBold(document.queryCommandState("bold"));
      setIsItalic(document.queryCommandState("italic"));
    };
    document.addEventListener("selectionchange", handler);
    return () => document.removeEventListener("selectionchange", handler);
  }, []);

  // ── Save (EXACT same API as DiaryEntry.jsx) ──
  const handleSave = useCallback(async () => {
    const text = getPlainText().trim();
    console.log("PAYLOAD TO DB:", JSON.stringify(text));
    if (!text || loading) return;
    if (!auth.currentUser) {
      setError("Please log in to save your diary entry.");
      return;
    }
    setLoading(true); setError(""); setInsight(null); setSaved(false);
    try {
      const res = await authFetch("/api/diary/analyze", {
        method: "POST",
        body: JSON.stringify({ text, title }),
      });
      const data = await res.json();
      if (res.status === 429) {
        setLimitReached(true);
        setError(data.error || "Monthly limit reached.");
        return;
      }
      if (!res.ok) {
        setError(data.error || "Something went wrong. Please try again.");
        return;
      }
      setInsight(data); setSaved(true);
      if (onEntrySaved) onEntrySaved(data);
    } catch {
      setError("Network error. Please check your connection.");
    } finally {
      setLoading(false);
    }
  }, [getPlainText, title, loading, onEntrySaved]);

const handleNewEntry = useCallback(() => {
  setTitle(""); setInsight(null); setError(""); setSaved(false); setLimitReached(false);
  setHasText(false);
  if (wordCountRef.current) wordCountRef.current.innerText = "0 words";
  if (editorRef.current) editorRef.current.innerHTML = "";
}, []);
  // ── Smooth vibe transition ──
  const switchVibe = useCallback((v) => {
    setVibe(v);
  }, []);

  // ═══ INSIGHT VIEW ═══
  if (saved && insight) {
    const mc = MOOD_COLORS[insight.mood] || MOOD_COLORS.neutral;
    const me = MOOD_EMOJI[insight.mood] || "💙";
    return (
      <div className="id-insight-view" style={{ background: vibe.bg, color: vibe.ink }}>
        <div className="id-insight-card" style={{ background: vibe.cardBg, borderColor: vibe.border }}>
          <div className="id-insight-header">
            <span className="id-insight-emoji" style={{ background: `${mc}18`, color: mc }}>{me}</span>
            <div>
              <div className="id-insight-mood" style={{ color: mc }}>{insight.mood}</div>
              <div className="id-insight-date" style={{ color: vibe.inkSoft }}>{dateStr}</div>
            </div>
          </div>
          {title && <div className="id-insight-title" style={{ color: vibe.titleColor }}>{title}</div>}
          <div className="id-insight-text" style={{ color: vibe.ink }}>{insight.insight}</div>

          {insight.isCrisis && (
            <div className="id-crisis">
              <p>💙 If you're in immediate distress, please reach out:</p>
              <a href="tel:9152987821" className="id-crisis-btn">📞 iCall — 9152987821</a>
            </div>
          )}
        </div>

        <div className="id-insight-actions">
          <button className="id-action-secondary" onClick={handleNewEntry}
            style={{ borderColor: vibe.border, color: vibe.ink }}>
            ✏️ Write Another
          </button>
          <button className="id-action-primary" onClick={() => navigate("/journal")}
            style={{ background: vibe.accent }}>
            📊 View Reports
          </button>
        </div>
      </div>
    );
  }

  // ═══ WRITING VIEW ═══
  return (
    <div className="id-root" style={{
      "--id-bg": vibe.bg,
      "--id-ink": vibe.ink,
      "--id-ink-soft": vibe.inkSoft,
      "--id-placeholder": vibe.placeholder,
      "--id-accent": vibe.accent,
      "--id-title-color": vibe.titleColor,
      "--id-margin": vibe.margin,
      "--id-card-bg": vibe.cardBg,
      "--id-border": vibe.border,
      transition: "all .5s ease",
      background: vibe.bg,
    }}>

      {/* ═══ VIBE SELECTOR ═══ */}
      <div className="id-vibe-bar">
        <span className="id-vibe-label">Vibe</span>
        <div className="id-swatches">
          {VIBES.map(v => (
            <button key={v.id}
              className={`id-swatch${vibe.id === v.id ? " on" : ""}`}
              style={{ background: v.swatch }}
              onClick={() => switchVibe(v)}
              aria-label={v.name}
              title={v.name}
            />
          ))}
        </div>
      </div>

      {/* ═══ THE PAPER ═══ */}
      <div className="id-paper" style={{
        backgroundImage: `repeating-linear-gradient(${vibe.paper})`,
        backgroundSize: "100% 32px",
        borderColor: vibe.border,
        transition: "all .5s ease",
      }}>
        {/* Red margin line */}
        <div className="id-margin-line" style={{ borderColor: vibe.margin }} />

        {/* Date */}
        <div className="id-date" style={{ color: vibe.inkSoft }}>
          {dateStr}
        </div>

        {/* Title */}
        <input
          className="id-title"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="Untitled entry…"
          style={{ color: vibe.titleColor, "--ph": vibe.placeholder }}
        />

        {/* Format toolbar */}
        <div className="id-toolbar" style={{ borderColor: vibe.border }}>
          <button className={`id-fmt${isBold ? " on" : ""}`}
            onClick={toggleBold} style={{ color: isBold ? vibe.accent : vibe.inkSoft }}
            aria-label="Bold"><strong>B</strong></button>
          <button className={`id-fmt${isItalic ? " on" : ""}`}
            onClick={toggleItalic} style={{ color: isItalic ? vibe.accent : vibe.inkSoft }}
            aria-label="Italic"><em>I</em></button>
          <div className="id-toolbar-spacer" />
          <span ref={wordCountRef} className="id-wc" style={{ color: vibe.placeholder }}>
  0 words
</span>
        </div>

        {/* Content editor
            white-space:pre-wrap  → spaces and newlines are preserved visually
            word-break:break-word → long words don't overflow */}
        <div
          ref={editorRef}
          className="id-editor"
          contentEditable
          suppressContentEditableWarning
          onInput={updateWordCount}
          data-placeholder="Today I felt… what happened was… I've been thinking about…"
          style={{
            color: vibe.ink,
            "--ph": vibe.placeholder,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}
        />
      </div>

      {/* ═══ ERROR ═══ */}
      {error && (
        <div className="id-error" style={{
          background: limitReached ? "#fff8f0" : "#fef2f2",
          color: limitReached ? "#c47a20" : "#b91c1c",
          borderColor: limitReached ? "#f0d4a8" : "#fca5a5",
        }}>
          {error}
          {limitReached && (
            <a href="/pricing" className="id-upgrade-link" style={{ color: vibe.accent }}>
              Upgrade to Plus →
            </a>
          )}
        </div>
      )}

      {/* ═══ SAVE BUTTON ═══ */}
      <button
  className="id-save"
  onClick={handleSave}
  disabled={!hasText || loading || limitReached}
  style={{ background: loading ? vibe.border : vibe.accent }}
>
        {loading ? (
          <span className="id-save-loading">
            <span className="id-save-dot" /><span className="id-save-dot" /><span className="id-save-dot" />
            <span style={{ marginLeft: 8 }}>Saksham is reading…</span>
          </span>
        ) : (
          <>✨ Save & Get Insight</>
        )}
      </button>
    </div>
  );
}